import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  LinearProgress,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import Tagssearch from "../../../assets/TagsSearch.svg"
import TagsFilter from "../../../assets/TagsFilter.svg";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import eventEmitter from "../../../components/utils/eventEmitter";
import DrawerWrapper from "../../../components/drawer/Drawer";
import TagsOverlay from "./TagsOverlay";
import WhiteTooltip from "../../../components/WhiteTooltip";
import { useDashboardContext } from "../DashboardContext";

interface TagData {
  name: string;
  percentage: number;
  color: string;
}

interface TagsResponse {
  status: number;
  message: string;
  data: {
    tags_count: number;
    tags: {
      [key: string]: number;
    };
  };
}

interface TagsProps {
  height?: string | number;
}

const Tags: React.FC<TagsProps> = ({ height = 420 }) => {
  const theme = useTheme();
  const { setIsOverlayOpen } = useDashboardContext();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState<TagData[]>([]);
  const [filteredTags, setFilteredTags] = useState<TagData[]>([]);
  const [showContent, setShowContent] = useState(false);
  const [totalTags, setTotalTags] = useState(0);

  const [trigger, setTrigger] = useState(0); // Used to re-trigger the effect
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tag, setTag] = useState("");
  const [sortBy, setSortBy] = useState("percentage-asc");
  const [searchFilter, setSearchFilter] = useState("");

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(drawerOpen);
  }, [drawerOpen, setIsOverlayOpen]);

  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1); // Changing this will re-trigger the effect
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  // Function to sort tags based on the selected sort option
  const sortTags = (tagsToSort: TagData[], sortOption: string): TagData[] => {
    const sortedTags = [...tagsToSort];

    switch (sortOption) {
      case "percentage-asc":
        return sortedTags.sort((a, b) => a.percentage - b.percentage);
      case "percentage-desc":
        return sortedTags.sort((a, b) => b.percentage - a.percentage);
      default:
        return sortedTags;
    }
  };

  // Function to filter tags based on search input
  const filterTags = (
    tagsToFilter: TagData[],
    searchTerm: string
  ): TagData[] => {
    if (!searchTerm.trim()) {
      return tagsToFilter;
    }

    return tagsToFilter.filter((tag) =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Effect to handle sorting and filtering when data or options change
  useEffect(() => {
    const filtered = filterTags(tags, searchFilter);
    const sorted = sortTags(filtered, sortBy);
    setFilteredTags(sorted);
  }, [tags, sortBy, searchFilter]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get<TagsResponse>(
          END_POINTS.DASHBOARD_TAGS
        );

        if (response.data && response.data.data) {
          // Set total tags count
          setTotalTags(response.data.data.tags_count);

          // Convert the API response to the format needed by the component
          const tagData: TagData[] = Object.entries(
            response.data.data.tags || {}
          ).map(([name, value]) => ({
            name,
            percentage: value,
            color: "#4432F8",
          }));
          // Sort by percentage in ascending order
          tagData.sort((a, b) => a.percentage - b.percentage);

          setTags(tagData);
        }
      } catch (error) {
        console.error("Error fetching tags data:", error);
        setTags([]);
        setTotalTags(0);
      } finally {
        setLoading(false);
        setShowContent(true);
      }
    };

    fetchData();
  }, [trigger]);

  const handleTagClick = (tag: string) => {
    setTag(tag);
    setDrawerOpen(true);
  };

  const onDrawerClose = () => {
    setTag("");
    setDrawerOpen(false);
  };

  const handleSortToggle = () => {
    setSortBy((prevSort) =>
      prevSort === "percentage-asc" ? "percentage-desc" : "percentage-asc"
    );
  };

  if (loading) {
    return (
      <Box sx={{ height }}>
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: "4px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
            // boxShadow: theme.palette.mode === "dark"
            //   ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
            //   : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)"
              }`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              height: 45,
              display: "flex",
              alignItems: "center",
              px: 2,
              bgcolor: theme.palette.mode === "dark" ? "#191C24" : "#FFFFFF",
            }}
          >
            <Skeleton variant="text" width={100} height={24} />
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.05)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.2)",
                borderRadius: "10px",
              },
            }}
          >
            {[...Array(5)].map((_, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Skeleton variant="text" width="70%" height={20} />
                  <Skeleton variant="text" width={40} height={20} />
                </Box>
                <Skeleton
                  variant="rectangular"
                  height={6.5}
                  sx={{
                    borderRadius: 4,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!showContent) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: "4px",
          height: height,
          width: "auto",
          bgcolor: theme.palette.mode === "dark" ? "#191C24" : "white",
          // boxShadow: theme.palette.mode === "dark"
          //   ? "0px 4px 12px rgba(0, 0, 0, 0.3)"
          //   : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          border: `1px solid ${theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.1)"
            }`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            height: 45,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            bgcolor: theme.palette.mode === "dark" ? "#191C24" : "#FFFFFF",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "13px", pr: 1 }}>
            Tags
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              variant="standard"
              placeholder="Search"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{
                width: "150px",
                color: theme.palette.text.primary,
                borderRadius: "6px",
                border: "1px solid",
                borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                transition: "all 0.3s ease",
                "&:hover": {
                    borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.24)" : "rgba(0, 0, 0, 0.24)",
                },
                "&:focus-within": {
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : "",
                    boxShadow: theme.palette.mode === 'dark' ? `0 0 0 2px ${theme.palette.primary.main}25` : "",
                    backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                },
                "& .MuiInputBase-root": {
                    height: "32px !important",
                    minHeight: "32px !important",
                    alignItems: "center",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                },
                "& .MuiInputBase-input": {
                    color: theme.palette.mode === 'dark' ? "#ffffff" : "inherit",
                    lineHeight: "32px",
                    padding: "0px !important",
                    fontSize: "12px",
                },
                "& .MuiInputBase-input::placeholder": {
                    color: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
                    opacity: 0.8,
                    fontSize: "12px",
                },
                "& fieldset": { border: "none" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {/* <Search 
                      sx={{ 
                        fontSize: "16px", 
                        color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)" 
                      }} 
                    /> */}
                    <img
                      src={Tagssearch}
                      alt="search"
                      style={{
                        width: "12px",
                        height: "16px",
                        cursor: "default",
                        filter:
                          theme.palette.mode === "dark"
                            ? "brightness(0) invert(1)"
                            : "none",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchFilter("")}>
                      <ClearIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </InputAdornment>
                ),
                disableUnderline: true,
              }}
            />
            <Box
              className="no-drag clickable-area"
              onClick={handleSortToggle}
              sx={{
                padding: "4px",
                borderRadius: "4px",
                border:
                  theme.palette.mode === "dark"
                    ? "1px solid #2D2D2D"
                    : "1px solid #E9E9ED",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#191C24" : "#FFFFFF",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "#2D2D2D"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <WhiteTooltip title="Security Score Sort" arrow>
                <img
                  src={TagsFilter}
                  alt="Toggle Sort"
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                    filter:
                      theme.palette.mode === "dark"
                        ? "brightness(0) invert(1)"
                        : "none",
                  }}
                />
              </WhiteTooltip>
            </Box>
          </Box>
        </Box>
        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.05)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(0, 0, 0, 0.2)",
              borderRadius: "10px",
            },
          }}
        >
          {filteredTags.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: theme.palette.text.secondary,
              }}
            >
              <Typography>
                {searchFilter ? "No tags match your search" : "No tags found"}
              </Typography>
            </Box>
          ) : (
            filteredTags.map((tag) => (
              <Box
                key={tag.name}
                className="no-drag clickable-area"
                sx={{ mb: 2, cursor: "pointer" }}
                onClick={() => handleTagClick(tag.name)}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ maxWidth: "70%", fontWeight: "700" }}
                  >
                    {tag.name.charAt(0).toUpperCase() + tag.name.slice(1)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme.palette.mode === "dark" ? "#FFFFFF" : "#3C32F8",
                      fontWeight: "700",
                    }}
                  >
                    {tag.percentage}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={tag.percentage}
                  sx={{
                    height: 6.5,
                    borderRadius: 4,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#4432F8",
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>
            ))
          )}
        </Box>
      </Paper>
      <DrawerWrapper
        styles={{ width: "50%", padding: 0, margin: 0 }}
        open={drawerOpen}
        onClose={onDrawerClose}
        header={
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography fontSize="16px" fontWeight={700}>
              {tag.charAt(0).toUpperCase() + tag.slice(1)}
            </Typography>
          </div>
        }
        body={<TagsOverlay tag={tag} />}
      // styles={{ width: "50%", padding: 0, margin: 0 }}
      />
    </>
  );
};

export default Tags;
